import React, { useState, useEffect } from "react";
import styled from "styled-components/macro";
import { createId, createDataTestId } from "./utils/helpers";
import Question, { Optional } from "./Question";
import Label from "./Label";
import Hint from "./Hint";
import Input from "./Input";
import Textarea from "./Textarea";
import ErrorMessage from "./ErrorMessage";
import { SecondaryButton } from "./Button";

function TextQuestion(props) {
  const componentName = props.name ? props.name : null;
  const componentId = createId("input", props.question, componentName);
  const dataTestId = createDataTestId("input", props.question, componentName);

  const propsValue = props.value || "";
  const [rawValue, setRawValue] = useState(propsValue);

  useEffect(() => {
    if (propsValue !== rawValue.trim()) {
      setRawValue(propsValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally omit rawValue to avoid infinite loop; this syncs local state when prop changes
  }, [propsValue]);

  const valueEntered = (newRawValue) => {
    setRawValue(newRawValue);
    props.valueEntered(newRawValue.trim());
  };

  // Ensure we don't pass children to the <input> element as that would result
  // in an error.
  const { children, ...inputProps } = props;

  return (
    <Question>
      <Label htmlFor={componentId}>
        {props.question} {props.optional ? <Optional /> : null}
        {props.hint ? <Hint>{props.hint}</Hint> : null}
        {props.validationError ? <ErrorMessage>{props.validationError}</ErrorMessage> : null}
      </Label>
      <Input
        {...inputProps}
        type={props.type ? props.type : "text"}
        onChange={(event) => valueEntered(event.target.value)}
        onFocus={props.validate}
        onBlur={props.validate}
        id={componentId}
        data-testid={dataTestId}
        value={rawValue}
        error={props.validationError ? props.validationError : null}
        disabled={props.disabled}
        size={props.size}
      />
      {children}
    </Question>
  );
}

export function TextareaQuestion(props) {
  const componentName = props.name ? props.name : null;
  const componentId = createId("textarea", props.question, componentName);
  const dataTestId = createDataTestId("textarea", props.question, componentName);

  const propsValue = props.value || "";
  const [rawValue, setRawValue] = useState(propsValue);

  useEffect(() => {
    if (propsValue !== rawValue.trim()) {
      setRawValue(propsValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally omit rawValue to avoid infinite loop; this syncs local state when prop changes
  }, [propsValue]);

  const valueEntered = (newRawValue) => {
    setRawValue(newRawValue);
    props.valueEntered(newRawValue.trim());
  };

  return (
    <Question className={props.className}>
      <Label htmlFor={componentId}>
        {props.question} {props.optional ? <Optional /> : null}
        {props.hint ? <Hint>{props.hint}</Hint> : null}
        {props.validationError ? <ErrorMessage>{props.validationError}</ErrorMessage> : null}
      </Label>
      <Textarea
        {...props}
        onChange={(event) => valueEntered(event.target.value)}
        onFocus={props.validate}
        onBlur={props.validate}
        id={componentId}
        data-testid={dataTestId}
        value={rawValue}
        error={props.validationError ? props.validationError : null}
        disabled={props.disabled}
        rows={props.rows || 5}
      />
    </Question>
  );
}

const QuestionButton = styled(SecondaryButton)`
  margin-top: 0.5em;
`;

export function TextQuestionWithButton(props) {
  const { buttonDisabled, buttonAction, buttonText, ...questionProps } = props;
  return (
    <TextQuestion {...questionProps}>
      <QuestionButton small disabled={buttonDisabled} onClick={buttonAction}>
        {buttonText}
      </QuestionButton>
    </TextQuestion>
  );
}

export default TextQuestion;
